import { LoadingOutlined } from '@ant-design/icons';
import { QueryClient } from '@tanstack/react-query';
import { Spin } from 'antd';
import Tooltip from 'antd/lib/tooltip';
import Clipboard from 'clipboard';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import Link from 'next/link';
import { getSession, useSession } from 'next-auth/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import {
  EmailIcon,
  FacebookIcon,
  FacebookMessengerIcon,
  FacebookMessengerShareButton,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  TwitterIcon,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from 'react-share';
import styled, { css } from 'styled-components';

import { ArrowLeftSvg, CrossSvg } from '../../../assets/icons';
import BookmarkSvg from '../../../assets/icons/BookmarkSvg';
import CuriBookmarksSvg from '../../../assets/icons/CuriBookmarks';
import FoldedHandsSvg from '../../../assets/icons/FoldedHandsSvg';
import QuestionMarkSvg from '../../../assets/icons/QuestionMarkSvg';
import ShareSvg from '../../../assets/icons/ShareSvg';
import BookmarkCuri from '../../../assets/images/BookmarkCuri.png';
import theme from '../../../assets/theme';
import { Input } from '../../../components/_atoms/_Input';
import { Button } from '../../../components/_atoms/Button';
import ExpandingButton from '../../../components/_atoms/ExpandingButton';
import { IconWrapper } from '../../../components/_atoms/IconWrapper';
import BaseModal from '../../../components/_molecules/BaseModal';
import { GeneratorCard } from '../../../components/_molecules/GeneratorCard';
import { NewLandingPageFooter } from '../../../components/_molecules/LandingPageFooter';
import LiveEmptyState from '../../../components/_molecules/LiveEmptyState';
import MSTeamsShareButton from '../../../components/_molecules/MSTeamsShareButton';
import QRCodeShareButton from '../../../components/_molecules/QRCodeShareButton';
import NewAppLayout from '../../../components/layouts/NewAppLayout';
import useAddToMyCollections from '../../../hooks/useAddToMyCollections';
import useAuthGuardPrompt from '../../../hooks/useAuthGuardPrompt';
import { useEditCollection } from '../../../hooks/useEditCollection';
import { useGetPublicCollectedGenerators } from '../../../hooks/useGetCollectedGenerators';
import { useMetrics } from '../../../hooks/useMetrics';
import usePaywallPrompt from '../../../hooks/usePaywallPrompt';
import useRoutePrefetch from '../../../hooks/useRoutePrefetch';
import useRouterMatch from '../../../hooks/useRouterMatch';
import useSSRGetSingleCollection from '../../../hooks/useSSRGetSingleCollection';
import { useMarketingMetrics } from '../../../providers/PlausibleAnalyticsProvider';
import { Collection } from '../../../services/backendService/types';
import CoreAPIService from '../../../services/CoreAPIService';
import { getBaseUrl } from '../../../utils/client';
import { detectAndBestEffortSetLanguage } from '../../../utils/localisationUtils';
import { dehydrateReactQueryPrefetchedInfiniteQuery } from '../../../utils/react-query-utils';
import { provisionFlags } from '../../../utils/ServerSideFeatureFlagUtils';
import { ResponsiveGeneratorsGridContainer } from '../../AuthenticatedHome';

const CollectionHead = () => {
  const { router } = useRouterMatch();
  const { collection } = useSSRGetSingleCollection(router.query.collectionId as string);

  if (!collection) return null;
  return (
    <Head>
      <title>{collection.collectionName} - Curipod collection</title>
      <meta
        property="og:title"
        content={`${collection.collectionName} - Curipod collection`}
        key="title"
      />
      <meta
        name="twitter:title"
        content={`${collection.collectionName} - Curipod collection`}
      />
      <meta
        name="description"
        content={
          `${collection.collectionName} | Explore a lovingly curated collection of customizable lessons, handpicked by one of our dedicated Curipod teachers, use AI to generate pedagogical activities and enhance your classroom experience.` ||
          ''
        }
        key="namedescription"
      />
      <meta
        property="description"
        content={
          `${collection.collectionName} | Explore a lovingly curated collection of customizable lessons, handpicked by one of our dedicated Curipod teachers, use AI to generate pedagogical activities and enhance your classroom experience.` ||
          ''
        }
        key="description"
      />
      <meta
        property="og:description"
        content={
          `${collection.collectionName} | Explore a lovingly curated collection of customizable lessons, handpicked by one of our dedicated Curipod teachers, use AI to generate pedagogical activities and enhance your classroom experience.` ||
          ''
        }
        key="og:description"
      />
      <meta
        name="twitter:description"
        content={
          `${collection.collectionName} | Explore a lovingly curated collection of customizable lessons, handpicked by one of our dedicated Curipod teachers, use AI to generate pedagogical activities and enhance your classroom experience.` ||
          ''
        }
      />
      <meta property="og:image" content={BookmarkCuri.src || ''} key="imageurl" />
      <meta
        property="og:image:secure_url"
        content={BookmarkCuri.src}
        key="imagesecureurl"
      />
      <meta property="og:type" content="website" />

      <meta property="og:url" content={`${getBaseUrl()}${router.asPath}`} key="ogurl" />
      <meta property="og:image:type" content={'image/png'} key="imagetype" />
      <meta
        property="og:image:alt"
        content={`${collection.collectionName} - Curipod collection` || ''}
        key="imageAlt"
      />
      <meta property="og:image:width" content={'160'} key="imageWidth" />
      <meta property="og:image:height" content={'108'} key="imageHeight" />
      <meta name="twitter:image" content={BookmarkCuri.src || ''} />
      <meta name="twitter:site" content={'@curipodofficial'} />
      <meta name="twitter:card" content={'summary'} />
      <meta name="twitter:widgets:theme" content="light" />
      <meta name="twitter:url" content={`${getBaseUrl()}${router.asPath}`} />
    </Head>
  );
};

const SingleCollectionPage = () => {
  const session = useSession();
  const { router } = useRouterMatch();
  const { collection } = useSSRGetSingleCollection(router.query.collectionId as string);

  // Preview page
  useRoutePrefetch(`/[tenantId]/lessons/[templateId]`, undefined, {
    priority: true,
  });

  return (
    <>
      <NewAppLayout allowUnAuthenticated={true}>
        <PageContainer $isAuthenticated={session.status === 'authenticated'}>
          <CollectionHead />

          {!collection && (
            <EmptyStateContainer>
              <LiveEmptyState
                maxWidth={500}
                icon={<FoldedHandsSvg />}
                title="Oops we couldnt find this collection!"
              />
            </EmptyStateContainer>
          )}
          {collection && (
            <SingleCollection
              collection={collection}
              isAuthenticated={session.status === 'authenticated'}
              uid={session.data?.user?.uid || ''}
            />
          )}
          <NewLandingPageFooter footerBackgroundColor={theme.colors.paleBeige} />
        </PageContainer>
      </NewAppLayout>
    </>
  );
};

const PageContainer = styled.div<{ $isAuthenticated: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  background: ${theme.colors.background};

  @media (min-width: 800px) {
    padding-left: ${({ $isAuthenticated }) => ($isAuthenticated ? '240px' : '0px')};
  }
`;

const ShareCollection = ({
  collectionName,
  collectionId,
}: {
  collectionName: string;
  collectionId: string;
}) => {
  const { tenantId } = useRouterMatch();
  const templateRef = useRef(new Clipboard('#copy_url_collection'));
  const [copyCollectionSuccess, setCopyCollectionSuccess] = useState(false);
  const metrics = useMetrics();
  const session = useSession();
  const { track } = useMarketingMetrics();
  const handleClickShareButton = useCallback(
    (type: string) => {
      if (session.status === 'authenticated') {
        metrics.logEvent('Collection.Share.Social.Media.Click', {
          someType: type,
          tenantId,
        });
      } else {
        track('Collection.Share.Social.Media.Click', { props: { someType: type } });
      }
    },
    [metrics, session.status, tenantId, track],
  );
  //url-encode the url when we redirect incase there are special symbols
  const removeSymbolsFromCollectionName = encodeURIComponent(collectionName);

  const shareUrl = `${getBaseUrl()}/collections/${removeSymbolsFromCollectionName}/${collectionId}`;

  const shareTitle = 'Check out my collection of Curipod customizable lessons';
  const emailSubject = 'Check out my collection of Curipod customizable lessons';

  useEffect(() => {
    const clip = templateRef.current;
    metrics.logEvent('Collection.Share.Copy.Link.Click', { collectionId, tenantId });
    clip.on('success', () => {
      setCopyCollectionSuccess(true);
    });

    return () => {
      clip.destroy();
    };
  }, [collectionId, metrics, tenantId]);

  useEffect(() => {
    const ref = setTimeout(() => {
      if (copyCollectionSuccess) {
        track('Share.Collection.Copy.Success', {
          props: {
            collectionId: collectionId,
            from: 'collectionPage',
          },
        });

        setCopyCollectionSuccess(false);
      }
    }, 3000);
    return () => {
      clearTimeout(ref);
    };
  }, [copyCollectionSuccess, metrics, track, session.status, collectionId]);

  return (
    <>
      <h1 style={{ width: 'fit-content' }}>
        <Tooltip title={'Share this collection with your colleagues.'}>
          Share &quot;{collectionName}&quot; collection
          <TooltipIconWrapper>
            <QuestionMarkSvg />
          </TooltipIconWrapper>
        </Tooltip>
      </h1>

      <p style={{ fontWeight: 'bold' }}>
        {
          'Anyone with this link can make a copy of your collection and continue to add to it. Your collection will always remain unchanged.'
        }
      </p>

      <SocialMediaContainer>
        <TwitterShareButton
          url={shareUrl}
          title={`${shareTitle}`}
          onClick={() => handleClickShareButton('twitter')}
        >
          <TwitterIcon size={32} round={true} />
        </TwitterShareButton>
        <LinkedinShareButton
          url={shareUrl}
          title={shareTitle}
          onClick={() => handleClickShareButton('linkedin')}
        >
          <LinkedinIcon size={32} round={true} />
        </LinkedinShareButton>
        <FacebookShareButton
          url={shareUrl}
          quote={shareTitle}
          onClick={() => handleClickShareButton('facebook')}
        >
          <FacebookIcon size={32} round={true} />
        </FacebookShareButton>
        <FacebookMessengerShareButton
          url={shareUrl}
          title={shareTitle}
          appId="857396192234237"
          onClick={() => handleClickShareButton('facebook')}
        >
          <FacebookMessengerIcon size={32} round={true} />
        </FacebookMessengerShareButton>
        <QRCodeShareButton url={shareUrl} />
        <a
          href={`mailto:?subject=${emailSubject}&body=${'Below is a link to my collection of Curipod generators.'}\n\n\n ${shareUrl}`}
          onClick={() => handleClickShareButton('email')}
        >
          <EmailIcon size={32} round={true} />
        </a>
        <WhatsappShareButton
          url={shareUrl}
          title={shareTitle}
          onClick={() => handleClickShareButton('whatsapp')}
        >
          <WhatsappIcon size={32} round={true} />
        </WhatsappShareButton>
        <MSTeamsShareButton url={shareUrl} body={shareTitle} />
      </SocialMediaContainer>
      <JoinLinkContainer>
        <InputContainer
          role="button"
          id="copy_url_collection"
          data-clipboard-text={shareUrl}
          $isCopied={copyCollectionSuccess}
        >
          <Input style={{ flex: '1' }} value={shareUrl} />
        </InputContainer>
      </JoinLinkContainer>
    </>
  );
};

const SocialMediaContainer = styled.div`
  display: flex;
  padding-bottom: 0.5em;
  & > *:not(:first-child) {
    margin-left: 2px;
  }
  & > *:not(:last-child) {
    margin-right: 2px;
  }
`;

const TooltipIconWrapper = styled.span`
  margin-left: 10px;
  svg {
    height: 20px;
    width: 20px;
    color: ${theme.colors.black};
  }
`;

const EmptyStateContainer = styled.div<{ $isGeneratorRow?: boolean }>`
  margin-top: 40px;
  padding: 20px;
  div > svg {
    height: 150px !important;
    width: 200px !important;
  }
`;

const JoinLinkContainer = styled.div`
  display: flex;

  @media (max-width: 690px) {
    flex-direction: column;

    button {
      margin-top: 10px;
      max-width: max-content;
      align-self: center;
    }
  }
`;
const InputContainer = styled.div<{ $isCopied: boolean }>`
  display: flex;
  cursor: pointer;
  position: relative;
  width: 100%;
  margin-right: 5px;
  border-radius: 10px;

  input {
    cursor: pointer;
    ${({ $isCopied }) => $isCopied && `border: solid 1px ${theme.colors.green}`}
  }

  &:after {
    ${({ $isCopied }) =>
      $isCopied
        ? css`
            content: 'Copied';
            background-color: ${theme.colors.green} !important;
            color: white;
          `
        : css`
            content: 'Copy link';
          `}
    position: absolute;
    right: 3px;
    top: 3px;
    border-radius: 10px;
    font-weight: 700;
    background: #fff;
    padding: 7px;
  }

  @media (max-width: 690px) {
    input {
      cursor: pointer;
      font-size: 16px;
    }

    &:after {
      font-size: 16px;
      top: 3px;
    }
  }
`;

const SingleCollection = ({
  collection,
  isAuthenticated,
  uid,
}: {
  collection: Collection;
  isAuthenticated: boolean;
  uid: string;
}) => {
  const [showShareModal, setShowShareModal] = useState(false);
  const { authGuardComponent, showAuthGuard } = useAuthGuardPrompt();

  const { tenantPlan } = usePaywallPrompt({
    startState: { screen: 'schoolPlanPaywall' },
  });
  const metrics = useMetrics();
  const { router } = useRouterMatch();
  const { tenantId } = useRouterMatch();
  const {
    collectedGenerators,
    isFetchingCollectedGenerators,
    isFetchingNextPageCollectedGenerators,
    hasNextPageCollectedGenerators,
    fetchNextPageCollectedGenerators,
  } = useGetPublicCollectedGenerators(collection.collectionId);
  const { removeGeneratorFromCollection } = useEditCollection(tenantId);
  const { saveToMyCollections, isLoading, isSuccess } = useAddToMyCollections(tenantId);

  const { ref: scrollRef, inView } = useInView();
  useEffect(() => {
    if (
      inView &&
      hasNextPageCollectedGenerators &&
      !isFetchingNextPageCollectedGenerators
    ) {
      fetchNextPageCollectedGenerators();
    }
  }, [
    inView,
    hasNextPageCollectedGenerators,
    isFetchingNextPageCollectedGenerators,
    fetchNextPageCollectedGenerators,
  ]);

  //if the user is authenticated - check if they own this collection
  const isCollectionOwner = useMemo(() => {
    if (isAuthenticated && collection.collectionCreatorId === uid) {
      return true;
    }

    return false;
  }, [isAuthenticated, collection, uid]);

  const handleSaveCollection = () => {
    if (!isAuthenticated) {
      showAuthGuard();
    } else if (!isCollectionOwner) {
      metrics.logEvent('Save.CopyToMyCollection.Click', {
        tenantId,
        collectionId: collection.collectionId,
        collectionName: collection.collectionName,
        collectionCreatorId: collection.collectionCreatorId,
        parentId: collection.parentId,
      });
      saveToMyCollections({
        collectionId: collection.collectionId,
        activeTenantId: tenantId,
      });
    }
  };

  const saveToCollectionsText = useMemo(() => {
    if (isCollectionOwner) {
      return 'Back to my collections';
    } else if (isLoading) {
      return 'Saving...';
    } else if (isSuccess) {
      return 'Saved!';
    } else {
      return 'Save to my collections';
    }
  }, [isCollectionOwner, isLoading, isSuccess]);
  return (
    <CollectionContainer>
      {authGuardComponent}
      <BaseModal visible={showShareModal} onCancel={() => setShowShareModal(false)}>
        <ShareCollection
          collectionName={collection.collectionName}
          collectionId={collection.collectionId}
        />
      </BaseModal>
      <Title $fontSize={36}>{collection.collectionName}</Title>
      <ButtonsContainer>
        {(isCollectionOwner || isSuccess) && (
          <IconWrapper
            height="20px"
            width="20px"
            style={{ display: 'flex', alignItems: 'center', gap: 5 }}
            onClick={() => router.push(`/${tenantId}/collections`)}
          >
            <ArrowLeftSvg />
            <p style={{ margin: 0, fontSize: 16 }}>{saveToCollectionsText}</p>
          </IconWrapper>
        )}
        {!isCollectionOwner && !isSuccess && (
          <Button onClick={handleSaveCollection} type="linkStyle" icon={<BookmarkSvg />}>
            {saveToCollectionsText}
          </Button>
        )}
        {collectedGenerators.length > 0 && (
          <Button
            onClick={() => setShowShareModal(true)}
            type="linkStyle"
            icon={<ShareSvg />}
          >
            Share
          </Button>
        )}
      </ButtonsContainer>
      {collectedGenerators.length === 0 && !isFetchingCollectedGenerators && (
        <LiveEmptyState
          maxWidth={500}
          icon={<CuriBookmarksSvg />}
          title="Oops looks like this collection is empty!"
        />
      )}
      <div style={{ height: '100%', width: '100%' }}>
        <ResponsiveGeneratorsGridContainer>
          {collectedGenerators &&
            collectedGenerators.map(generator => {
              return (
                <>
                  {isCollectionOwner ? (
                    <div
                      key={generator.id}
                      style={{
                        position: 'relative',
                      }}
                    >
                      <Link
                        href={`/generators/${generator.title}/g/${generator.id}?collectionId=${collection.collectionId}`}
                        key={generator.id}
                      >
                        <a style={{ height: '100%', width: '100%' }}>
                          <GeneratorCard
                            subTitle=""
                            title={generator.title || ''}
                            coverImageId={generator.coverImageId}
                            status={generator.status}
                            firstPublished={generator.firstPublished}
                            isPremiumGenerator={generator?.aiContent?.isPremiumGenerator}
                            generatorPlan={generator?.aiContent?.generatorPlan}
                            description={generator.description || ''}
                            tenantPlan={tenantPlan}
                            learningStandards={generator.learningStandards}
                          />
                        </a>
                      </Link>
                      <ExpandingButton
                        buttonText={`Remove`}
                        handleClick={() =>
                          removeGeneratorFromCollection({
                            collectionId: collection.collectionId,
                            generatorId: generator.id,
                          })
                        }
                        style={{
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          fontSize: 13,
                          margin: 10,
                        }}
                        icon={<CrossSvg />}
                      />
                    </div>
                  ) : (
                    <Link
                      href={`/generators/${generator.title}/g/${generator.id}`}
                      key={generator.id}
                    >
                      <a style={{ height: '100%', width: '100%' }}>
                        <GeneratorCard
                          subTitle=""
                          title={generator.title || ''}
                          coverImageId={generator.coverImageId}
                          status={generator.status}
                          firstPublished={generator.firstPublished}
                          isPremiumGenerator={generator?.aiContent?.isPremiumGenerator}
                          generatorPlan={generator?.aiContent?.generatorPlan}
                          description={generator.description || ''}
                          tenantPlan={tenantPlan}
                          learningStandards={generator.learningStandards}
                        />
                      </a>
                    </Link>
                  )}
                </>
              );
            })}
        </ResponsiveGeneratorsGridContainer>

        {collectedGenerators && !isFetchingCollectedGenerators && (
          <div
            style={{
              height: 100,
              width: '100%',
              gridAutoColumns: '1 / -1',
              display: 'flex',
              justifyContent: 'center',
            }}
            ref={scrollRef}
          >
            {(isFetchingCollectedGenerators || isFetchingNextPageCollectedGenerators) && (
              <Spin
                indicator={
                  <LoadingOutlined
                    style={{ fontSize: 50, color: theme.colors.orange }}
                    spin
                  />
                }
              />
            )}
          </div>
        )}
      </div>
    </CollectionContainer>
  );
};

const CollectionContainer = styled.div`
  min-height: calc(100vh - 60px);
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 80px 20px 20px 20px;
  position: relative;
`;

const Title = styled.h3<{ $fontSize: number }>`
  text-align: left;
  width: 100%;
  font-weight: bold;
  font-size: ${({ $fontSize }) => $fontSize}px;
  line-height: 140%;
  color: ${theme.colors.black};
  display: flex;
  align-items: center;
  margin-bottom: 5px;
  padding-left: 30px;

  @media (max-width: 800px) {
    padding-left: 20px;
    margin-bottom: 0px;
  }

  @media (max-width: 690px) {
    font-size: ${({ $fontSize }) => $fontSize - 2}px;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  width: fit-content;
  gap: 20px;
  padding: 0 20px;

  button > svg {
    height: 20px;
    width: 20px;
  }
`;

export const getServerSideProps: GetServerSideProps = async context => {
  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=300, stale-while-revalidate=3600',
  );

  const queryClient = new QueryClient();
  const session = await getSession({ req: context.req });

  const { collectionId } = context.query as { collectionId: string };
  const flags = await provisionFlags(context, queryClient, session);

  await queryClient.prefetchQuery(
    ['collection-retrieve', collectionId],
    async () => {
      return CoreAPIService.fetchSingleCollection(collectionId);
    },
    {
      staleTime: 1000 * 60 * 5,
    },
  );

  return {
    props: {
      ...flags,
      dehydratedState: dehydrateReactQueryPrefetchedInfiniteQuery(queryClient),
      ...(await detectAndBestEffortSetLanguage(context)),
      session,
    },
  };
};

export default SingleCollectionPage;
